<template>
  <div class="login-page">
    <div class="login-card">
      <img src="/img/logopriadi_blue.png" class="login-logo" alt="PRiADI logo" />

      <h1 class="login-title">{{ $t('welcome') }}</h1>
      <p class="login-subtitle">{{ $t('loginPageSubtitle') }}</p>

      <form @submit.prevent="authenticate">
        <div class="form-floating mb-3">
          <input
            v-model="email"
            type="email"
            class="form-control"
            id="loginEmail"
            :placeholder="$t('emailHint')"
            autofocus />
          <label for="loginEmail"><i class="bi bi-envelope-fill me-2"></i> {{ $t('email') }}</label>
        </div>
        <p class="form-error-priadi d-block mb-3" v-if="errors.email">{{ errors.email }}</p>

        <div class="form-floating mb-4 position-relative">
          <input
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="form-control"
            id="loginPassword"
            :placeholder="$t('passwordHint')" />
          <label for="loginPassword"><i class="bi bi-key-fill me-2"></i> {{ $t('password') }}</label>
          <button
            type="button"
            class="login-eye"
            @click="showPassword = !showPassword"
            :aria-label="showPassword ? 'Hide password' : 'Show password'">
            <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'" aria-hidden="true"></i>
          </button>
        </div>
        <p class="form-error-priadi d-block mb-4" v-if="errors.password">{{ errors.password }}</p>

        <button
          class="btn-priadi btn-priadi-lg btn-priadi-block"
          type="submit"
          :disabled="isLoading">
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          {{ $t('signin') }}
        </button>
      </form>

      <p class="login-footer" v-if="currentRole === 'individual'">
        {{ $t('noaccount') }}
        <router-link to="/register" class="login-link">{{ $t('signup') }}</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'Login',
  data() {
    return {
      errors: {},
      email: '',
      password: '',
      showPassword: false,
      isLoading: false,
    }
  },
  computed: {
    currentRole() {
      return this.$store.getters.getRole
    }
  },
  mounted() {
    if (this.$store.getters.isAuth) {
      this.$router.push('/transactions')
    }
  },
  methods: {
    async authenticate() {
      this.errors = {}

      if (!this.email) {
        this.errors.email = this.$t('validation.required', { field: this.$t('email') })
      }
      if (!this.password) {
        this.errors.password = this.$t('validation.required', { field: this.$t('password') })
      }

      if (Object.keys(this.errors).length > 0) {
        return
      }

      this.isLoading = true
      try {
        await axios.post(
          '/api/login',
          {
            email: this.email,
            password: this.password,
          },
          { withCredentials: true }
        )

        this.$store.dispatch('setAuth', true)

        try {
          const response = await axios.get('/api/user', { withCredentials: true })
          this.$store.dispatch('setUser', response.data)
        } catch (error) {
          console.error('Failed to fetch user:', error)
          this.$store.dispatch('setUser', { id: null, email: null })
          this.$store.dispatch('setAuth', false)
          throw error
        }

        const nextRoute = this.currentRole === 'hr' ? '/transactions' : '/package'
        this.$router.push(nextRoute)
      } catch (error) {
        console.error('Authentication error:', error)
        const errorMsg = error.response?.data?.detail || 'An error occurred during login'
        Swal.fire({
          icon: 'error',
          title: 'Oops',
          text: errorMsg,
        })
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: linear-gradient(
      160deg,
      rgba(var(--color-primary-rgb), 0.88) 0%,
      rgba(var(--color-primary-rgb), 0.72) 100%
    ),
    url('/img/login.png');
  background-size: cover;
  background-position: center;
  padding: var(--space-6);
}

.login-card {
  width: 100%;
  max-width: 400px;
  background-color: var(--color-surface);
  border-radius: var(--radius-lg);
  padding: var(--space-8) var(--space-6);
  box-shadow: var(--shadow-lg);
}

.login-logo {
  display: block;
  width: 100px;
  margin: 0 auto var(--space-6);
}

.login-title {
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--color-text-primary);
  text-align: center;
  margin-bottom: var(--space-1);
}

.login-subtitle {
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  text-align: center;
  margin-bottom: var(--space-8);
}

.login-eye {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  padding: var(--space-2) var(--space-3);
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: color var(--transition-base);
}

.login-eye:hover {
  color: var(--color-text-primary);
}

.login-footer {
  text-align: center;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  margin-top: var(--space-6);
  margin-bottom: 0;
}

.login-link {
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  text-decoration: none;
}

.login-link:hover {
  color: var(--color-primary-hover);
  text-decoration: underline;
}
</style>
